import React, { useState, useMemo } from 'react';
import MetricCard from './MetricCard';
import {
  IconHeart, IconLungs, IconMapPin, IconFlame, IconScale, IconSun,
  IconWatch,
} from './Icons';
import { AppleExtras, AppleDailyMaps, HomeLeavingDay } from '../hooks/useDashboardData';
import { DailyMetrics } from '../types';
import { localDateString } from '../lib/dashboardHelpers';

type Range = 7 | 14 | 30;
const RANGE_OPTIONS: Range[] = [7, 14, 30];

interface Props {
  appleExtras: AppleExtras;
  appleDailyMaps: AppleDailyMaps;
  latest: DailyMetrics | null;
  homeLeaving?: HomeLeavingDay[];
  range: Range;
  onRangeChange: (r: Range) => void;
}

/* ── Helper: average values from a date map within a period ───────────── */
function avgMap(map: Record<string, number>, days: number): number | null {
  const today = localDateString();
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  const cutoffStr = localDateString(cutoff);
  const vals = Object.entries(map)
    .filter(([d]) => d >= cutoffStr && d < today)
    .map(([, v]) => v);
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

/** Extract last 7 days of values from a date map, sorted chronologically */
function sparkFromMap(map: Record<string, number>): number[] {
  const today = localDateString();
  return Object.entries(map)
    .filter(([d]) => d < today)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-7)
    .map(([, v]) => v);
}

/* ── Daylight card with expandable timing detail ─────────────────────── */
function DaylightCard({ value, unit, appleExtras }: { value: number | null; unit: string; appleExtras: AppleExtras }) {
  const [expanded, setExpanded] = useState(false);
  if (value == null) return null;

  const mins = Math.round(value);
  const onTarget = mins >= 30;
  const ml = appleExtras.morningLight;

  return (
    <div
      className="card flex flex-col gap-3 cursor-pointer hover:shadow-card-hover transition-shadow duration-150"
      onClick={() => setExpanded(e => !e)}
    >
      <div className="flex items-center justify-between">
        <span className="section-label">Daylight</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#F59E0B18' }}>
          <IconSun size={16} color="#F59E0B" />
        </div>
      </div>

      <div className="flex items-end gap-1.5">
        <span className="text-3xl font-bold text-arete-navy leading-none">{mins}</span>
        <span className="text-sm text-arete-muted mb-0.5">{unit}</span>
      </div>

      <div className={`flex items-center gap-1 text-xs font-medium ${onTarget ? 'text-arete-green' : mins >= 15 ? 'text-arete-muted' : 'text-arete-red'}`}>
        <span>{onTarget ? '↑' : mins >= 15 ? '→' : '↓'}</span>
        <span>{onTarget ? 'On target (30+ min)' : mins >= 15 ? 'Get more sun' : 'Low exposure'}</span>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 pt-2 mt-1 space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-arete-muted">First light</span>
            {ml ? (
              <span className={`font-medium ${ml.isMorning ? 'text-green-600' : 'text-amber-600'}`}>
                {ml.firstTimeStr} {ml.isMorning ? '✓' : '(after 10am)'}
              </span>
            ) : (
              <span className="text-arete-muted font-medium">No data yet</span>
            )}
          </div>
          {ml && (
            <div className="text-[11px] text-arete-muted">
              {ml.isMorning
                ? 'Morning light before 10am supports circadian rhythm'
                : 'Try to get sunlight before 10am for better sleep'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DashboardAppleWatch({ appleExtras, appleDailyMaps, latest, homeLeaving, range, onRangeChange }: Props) {
  // Compute period-aware values from daily maps
  const periodValues = useMemo(() => {
    const rhr = avgMap(appleDailyMaps.rhr, range);
    const spo2 = avgMap(appleDailyMaps.spo2, range);
    const distance = avgMap(appleDailyMaps.distance, range);
    const calories = avgMap(appleDailyMaps.calories, range);
    const daylight = avgMap(appleDailyMaps.daylight, range);
    const weight = avgMap(appleDailyMaps.weight, range);
    return { rhr, spo2, distance, calories, daylight, weight };
  }, [appleDailyMaps, range]);

  // Home-leaving: average over period
  const hlAvg = useMemo(() => {
    if (!homeLeaving || homeLeaving.length === 0) return null;
    const today = localDateString();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - range);
    const cutoffStr = localDateString(cutoff);
    const periodDays = homeLeaving.filter(d => d.date >= cutoffStr && d.date < today);
    if (periodDays.length === 0) return null;
    const avgMins = Math.round(periodDays.reduce((s, d) => s + d.awayMins, 0) / periodDays.length);
    return avgMins;
  }, [homeLeaving, range]);

  const isAvg = range > 1;
  const unitSuffix = isAvg ? '/day' : '';
  const periodLabel = `${range}-Day Daily Average`;

  const hasAny = periodValues.rhr || periodValues.spo2 || periodValues.distance
    || periodValues.weight || periodValues.calories || periodValues.daylight || hlAvg != null;

  if (!hasAny) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-label flex items-center gap-1.5">
          <IconWatch size={14} color="#64748B" /> Apple Watch
        </h3>
        <div className="flex items-center bg-gray-100 rounded-xl p-1 gap-1">
          {RANGE_OPTIONS.map(r => (
            <button
              key={r}
              onClick={() => onRangeChange(r)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                range === r ? 'bg-white text-arete-forest shadow-sm' : 'text-arete-muted hover:text-arete-forest'
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {periodValues.rhr != null && (
          <MetricCard label="Resting HR" value={Math.round(periodValues.rhr)} unit="bpm" icon={<IconHeart size={16} color="#EF4444" />} color="#EF4444" sparkline={sparkFromMap(appleDailyMaps.rhr)} />
        )}
        {periodValues.spo2 != null && (
          <MetricCard label="SpO₂" value={Math.round(periodValues.spo2 * 10) / 10} unit="%" icon={<IconLungs size={16} color="#3B82F6" />} color="#3B82F6" sparkline={sparkFromMap(appleDailyMaps.spo2)} />
        )}
        {periodValues.distance != null && (
          <MetricCard label="Distance" value={(Math.round(periodValues.distance * 10) / 10).toFixed(1)} unit={`mi${unitSuffix}`} icon={<IconMapPin size={16} color="#10B981" />} color="#10B981" sparkline={sparkFromMap(appleDailyMaps.distance)} />
        )}
        {periodValues.calories != null && (
          <MetricCard label="Active Cal" value={Math.round(periodValues.calories)} unit={`kcal${unitSuffix}`} icon={<IconFlame size={16} color="#F97316" />} color="#F97316" sparkline={sparkFromMap(appleDailyMaps.calories)} />
        )}
        {periodValues.weight != null && (
          <MetricCard label="Weight" value={(Math.round(periodValues.weight * 10) / 10).toFixed(1)} unit="lb" icon={<IconScale size={16} color="#8B5CF6" />} color="#8B5CF6" sparkline={sparkFromMap(appleDailyMaps.weight)} />
        )}
        <DaylightCard value={periodValues.daylight} unit={`min${unitSuffix}`} appleExtras={appleExtras} />
        {hlAvg != null && (() => {
          const mins = hlAvg;
          const hrs = Math.floor(mins / 60);
          const remMins = mins % 60;
          const display = mins >= 60 ? `${hrs}h ${remMins}m` : `${mins}`;
          const unit = mins >= 60 ? unitSuffix : `min${unitSuffix}`;
          const trend: 'up' | 'neutral' | 'down' = mins >= 60 ? 'up' : mins >= 15 ? 'neutral' : 'down';
          const label = mins >= 60 ? 'Active day' : mins >= 15 ? 'Mostly home' : mins > 0 ? 'Mostly home' : '';
          return (
            <MetricCard
              label="Time Outside"
              value={mins > 0 ? display : '—'}
              unit={unit}
              icon={<IconMapPin size={16} color="#10B981" />}
              color="#10B981"
              trend={mins > 0 ? trend : undefined}
              trendLabel={mins > 0 ? label : undefined}
            />
          );
        })()}
      </div>

      <p className="text-xs text-arete-muted text-center mt-3">{periodLabel}</p>
    </div>
  );
}
